import { THREE, COLORS } from '../config/ThreeConfig.js';
import { isTestEnvironment } from '../utils/runtimeEnv.js';
/**
 * Vibe Jam 2026 Portal Integration
 * Handles player transfer between games in the webring
 */


export class Portal {
  static config = null;
  static startPortal = null;
  static exitPortal = null;
  static startPortalActive = false;
  static particles = [];
  static animationId = null;
  static _isTravelling = false;

  static DEFAULT_START_POSITION = { x: -170, y: 105, z: -260 };
  static DEFAULT_EXIT_POSITION = { x: 170, y: 105, z: -260 };
  
  /**
   * Parse incoming portal data from URL
   */
  static parseIncoming(urlParams) {
    const speedRaw = urlParams.get('speed');
    const hpRaw = urlParams.get('hp');
    const speedValue = speedRaw === null ? Number.NaN : Number(speedRaw);
    const hpValue = hpRaw === null ? Number.NaN : Number(hpRaw);
    const data = {
      isPortal: urlParams.get('portal') === 'true',
      username: urlParams.get('username') || 'Mercenary',
      color: urlParams.get('color') || '#00c6ff',
      speed: Number.isFinite(speedValue) ? speedValue : 5,
      ref: urlParams.get('ref') || null,
      hp: Number.isFinite(hpValue) ? Math.trunc(hpValue) : 100,
      character: urlParams.get('character') || null,
      avatarUrl: urlParams.get('avatar_url') || null,
      team: urlParams.get('team') || null,
    };
    
    console.log('🌀 Portal data received:', data);
    return data;
  }
  
  /**
   * Initialize portal system
   */
  static init(options) {
    this.dispose();

    this.config = {
      scene: options.scene,
      getPlayer: options.getPlayer,
      uiManager: options.uiManager || null,
      spawnPoint: options.spawnPoint || this.DEFAULT_START_POSITION,
      exitPosition: options.exitPosition || this.DEFAULT_EXIT_POSITION,
      exitLabel: options.exitLabel || 'VIBE JAM PORTAL',
      collisionRadius: Math.max(24, Number(options.collisionRadius) || 36),
      onRailsCollision: options.onRailsCollision !== false,
      navigate: typeof options.navigate === "function"
        ? options.navigate
        : (url) => {
            window.location.href = url;
          },
    };
    this._isTravelling = false;
    
    // Check if player arrived via portal
    const urlParams = new URLSearchParams(window.location.search);
    const arrivedViaPortal = urlParams.get('portal') === 'true';
    const refUrl = this.normalizeRefUrl(urlParams.get('ref'));
    
    // Create start portal (for returning to previous game)
    if (arrivedViaPortal && refUrl) {
      this.createStartPortal(refUrl);
      this.config.uiManager?.showMessage?.('RETURN PORTAL ARMING: TOP LEFT', 'info', 2400);
      
      // Activate after 5 second grace period
      setTimeout(() => {
        this.startPortalActive = true;
        this.config.uiManager?.showMessage?.('RETURN PORTAL ONLINE', 'success', 1800);
      }, 5000);
    }
    
    // Create exit portal (to next game)
    this.createExitPortal();
    this.config.uiManager?.showMessage?.('VIBE JAM PORTAL ONLINE: TOP RIGHT', 'success', 2600);

    try {
      globalThis.__sm64PortalSnapshot = () => this.getSnapshot();
    } catch {
      // ignore
    }
    
    console.log('🌀 Portal system initialized');
  }
  
  /**
   * Create start portal (red - returns to previous game)
   */
  static createStartPortal(refUrl) {
    const portal = this.createPortalMesh(COLORS.ENEMY_BOSS, this.config.spawnPoint, 'RETURN TO PREVIOUS GAME');
    this.startPortal = {
      type: "return",
      mesh: portal.mesh,
      particles: portal.particles,
      box: portal.box,
      refUrl,
    };
    this.config.scene.add(this.startPortal.mesh);
  }
  
  /**
   * Create exit portal (green - goes to next random game)
   */
  static createExitPortal() {
    const portal = this.createPortalMesh(COLORS.POWERUP_SHIELD, this.config.exitPosition, this.config.exitLabel);
    this.exitPortal = {
      type: "exit",
      mesh: portal.mesh,
      particles: portal.particles,
      box: portal.box,
    };
    this.config.scene.add(this.exitPortal.mesh);
  }
  
  /**
   * Create portal mesh with particles
   */
  static createPortalMesh(color, position, label) {
    const group = new THREE.Group(); // Using ThreeConfig.THREE
    group.position.set(position.x, position.y, position.z);
    group.rotation.x = 0.35;
    
    // Torus ring
    const torusGeometry = new THREE.TorusGeometry(15, 2, 16, 100);
    const torusMaterial = new THREE.MeshPhongMaterial({
      color,
      emissive: color,
      emissiveIntensity: 0.5,
      transparent: true,
      opacity: 0.8,
    });
    const torus = new THREE.Mesh(torusGeometry, torusMaterial);
    group.add(torus);
    
    // Inner disc
    const discGeometry = new THREE.CircleGeometry(13, 32);
    const discMaterial = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.5,
      side: THREE.DoubleSide,
    });
    const disc = new THREE.Mesh(discGeometry, discMaterial);
    group.add(disc);
    
    // Label
    if (label && !isTestEnvironment()) {
      const canvas = document.createElement('canvas');
      canvas.width = 512;
      canvas.height = 64;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.fillStyle = '#' + color.toString(16).padStart(6, '0');
        ctx.font = 'bold 32px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(label, canvas.width / 2, canvas.height / 2 + 10);

        const labelTexture = new THREE.CanvasTexture(canvas);
        const labelMaterial = new THREE.MeshBasicMaterial({
          map: labelTexture,
          transparent: true,
          side: THREE.DoubleSide,
        });
        const labelMesh = new THREE.Mesh(
          new THREE.PlaneGeometry(30, 5),
          labelMaterial
        );
        labelMesh.position.y = 20;
        group.add(labelMesh);
      }
    }
    
    // Particle system
    const particleCount = 1000;
    const particleGeometry = new THREE.BufferGeometry();
    const positions = new Float32Array(particleCount * 3);
    const colors = new Float32Array(particleCount * 3);
    
    const r = ((color >> 16) & 0xff) / 255;
    const g = ((color >> 8) & 0xff) / 255;
    const b = (color & 0xff) / 255;
    
    for (let i = 0; i < particleCount * 3; i += 3) {
      const angle = Math.random() * Math.PI * 2;
      const radius = 15 + (Math.random() - 0.5) * 4;
      positions[i] = Math.cos(angle) * radius;
      positions[i + 1] = Math.sin(angle) * radius;
      positions[i + 2] = (Math.random() - 0.5) * 4;
      
      const jitter = 0.8 + Math.random() * 0.2;
      colors[i] = r * jitter;
      colors[i + 1] = g * jitter;
      colors[i + 2] = b * jitter;
    }
    
    particleGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    particleGeometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    
    const particleMaterial = new THREE.PointsMaterial({
      size: 0.2,
      vertexColors: true,
      transparent: true,
      opacity: 0.6,
    });
    
    const particles = new THREE.Points(particleGeometry, particleMaterial);
    group.add(particles);
    
    // Bounding box
    const box = new THREE.Box3().setFromObject(group);
    
    return { mesh: group, particles: particleGeometry, box };
  }

  static getPlayerWorldPosition(playerObject) {
    if (!playerObject) return null;
    const out = new THREE.Vector3();
    if (typeof playerObject.getWorldPosition === "function") {
      playerObject.getWorldPosition(out);
      return out;
    }
    if (playerObject.position?.isVector3) return playerObject.position.clone();
    return null;
  }

  static getPortalWorldPosition(portal) {
    if (!portal?.mesh) return null;
    const out = new THREE.Vector3();
    portal.mesh.getWorldPosition(out);
    return out;
  }

  static getPortalDistance(playerObject, portal) {
    const playerPos = this.getPlayerWorldPosition(playerObject);
    const portalPos = this.getPortalWorldPosition(portal);
    if (!playerPos || !portalPos) return Number.POSITIVE_INFINITY;

    // On-rails games do not give the player real Z travel. Treat portal entry as
    // flying into its screen-lane position while the stage streams toward the camera.
    if (this.config?.onRailsCollision !== false) {
      return Math.hypot(playerPos.x - portalPos.x, playerPos.y - portalPos.y);
    }
    return playerPos.distanceTo(portalPos);
  }

  static isCollidingWithPortal(playerObject, portal) {
    const radius = Math.max(24, Number(this.config?.collisionRadius) || 64);
    return this.getPortalDistance(playerObject, portal) <= radius;
  }

  static navigateTo(url) {
    if (this._isTravelling) return;
    this._isTravelling = true;
    try {
      this.config?.uiManager?.showMessage?.('WEBRING JUMP INITIATED', 'success', 1200);
    } catch {
      // ignore
    }
    this.config?.navigate?.(url);
  }
  
  /**
   * Animate portal particles
   */
  static animateParticles(particleGeometry) {
    const positions = particleGeometry.attributes.position.array;
    const time = Date.now() * 0.001;
    
    for (let i = 0; i < positions.length; i += 3) {
      positions[i + 1] += 0.05 * Math.sin(time + i);
    }
    
    particleGeometry.attributes.position.needsUpdate = true;
  }
  
  /**
   * Check portal collisions
   */
  static checkCollisions() {
    if (this._isTravelling) return;
    if (!this.isPortalTravelAllowed()) return;
    const player = this.config.getPlayer();
    if (!player) return;
    
    // Check start portal (return to previous game)
    if (this.startPortal && this.startPortalActive) {
      if (this.isCollidingWithPortal(player, this.startPortal)) {
        // Return to previous game: preserve params, but update ref to point back here.
        const urlParams = new URLSearchParams(window.location.search);
        urlParams.set('portal', 'true');
        urlParams.set('ref', window.location.origin);

        const targetUrl = new URL(this.startPortal.refUrl);
        targetUrl.search = urlParams.toString();
        this.navigateTo(targetUrl.toString());
      }
    }
    
    // Check exit portal (go to next game)
    if (this.exitPortal) {
      if (this.isCollidingWithPortal(player, this.exitPortal)) {
        // Build forwarded params
        const params = new URLSearchParams(window.location.search);
        params.set('portal', 'true');
        params.set('ref', window.location.origin);
        
        // Add player state if available
        if (window.game?.player) {
          params.set('username', window.game.player.username || 'Mercenary');
          params.set('character', window.game.player.character || 'honeybadger');
          params.set('hp', String(window.game.player.health ?? 100));
          if (window.game.player.color) params.set('color', window.game.player.color);
          if (typeof window.game.player.speed === 'number') params.set('speed', String(window.game.player.speed));
        }
        
        // Navigate to portal redirector
        this.navigateTo('https://vibej.am/portal/2026?' + params.toString());
      }
    }
  }

  static getSnapshot() {
    const start = this.startPortal ? this.getPortalWorldPosition(this.startPortal) : null;
    const exit = this.exitPortal ? this.getPortalWorldPosition(this.exitPortal) : null;
    return {
      startPortal: !!this.startPortal,
      startPortalActive: !!this.startPortalActive,
      exitPortal: !!this.exitPortal,
      startPosition: start?.toArray?.() || null,
      exitPosition: exit?.toArray?.() || null,
      collisionRadius: Math.max(24, Number(this.config?.collisionRadius) || 64),
      onRailsCollision: this.config?.onRailsCollision !== false,
      isTravelling: !!this._isTravelling,
      travelAllowed: this.isPortalTravelAllowed(),
    };
  }

  static isTruthyParam(value) {
    const v = String(value || "").trim().toLowerCase();
    return v === "1" || v === "true" || v === "yes" || v === "on";
  }

  static isPortalTravelAllowed() {
    try {
      const params = new URLSearchParams(window.location.search);
      if (this.isTruthyParam(params.get("portaltest"))) return true;
      const game = window.game;
      if (game?.state && game.state.isRunning !== true) return false;
      if (game?.state?.isPaused) return false;
      if (game?.autoplayEnabled) return false;
      if (this.isTruthyParam(params.get("autoplay")) || this.isTruthyParam(params.get("demo"))) return false;
    } catch {
      // ignore
    }
    return true;
  }

  static normalizeRefUrl(refValue) {
    if (!refValue) return null;
    try {
      // Already absolute
      const absolute = new URL(refValue);
      if (absolute.protocol === "http:" || absolute.protocol === "https:") return absolute.toString();
      return null;
    } catch {
      // If it's just a host (or host/path), assume https.
      try {
        const assumed = new URL(`https://${refValue}`);
        return assumed.toString();
      } catch {
        return null;
      }
    }
  }
  
  /**
   * Start animation loop (call every frame)
   */
  static startAnimationLoop() {
    this.animationId = requestAnimationFrame(() => this.animate());
  }
  
  /**
   * Animation loop
   */
  static animate() {
    this.animationId = requestAnimationFrame(() => this.animate());
    
    // Animate particles
    if (this.startPortal) {
      this.animateParticles(this.startPortal.particles);
    }
    if (this.exitPortal) {
      this.animateParticles(this.exitPortal.particles);
    }
    
    // Check collisions
    this.checkCollisions();
  }
  
  /**
   * Stop animation loop
   */
  static stopAnimationLoop() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }
  
  /**
   * Cleanup
   */
  static dispose() {
    this.stopAnimationLoop();
    
    if (this.config?.scene && this.startPortal) {
      this.config.scene.remove(this.startPortal.mesh);
    }
    if (this.config?.scene && this.exitPortal) {
      this.config.scene.remove(this.exitPortal.mesh);
    }

    this.startPortal = null;
    this.exitPortal = null;
    this.startPortalActive = false;
    this._isTravelling = false;
  }
}
